/**
 * Archives registry: served by GET /api/archives, with a hardcoded fallback
 * mirroring backend/archives/registry.py (docs/architecture.md) so the search
 * panel works before/without the backend.
 */
import type { PandoraSiteInfo } from './pandora';

export interface Archive {
  key: string;
  name: string;
  site: string;
  corsMedia: boolean;
  resolutions: number[];
  formats: string[];
  /**
   * A pandora-embeddings service indexing this archive's shot descriptions,
   * when the backend has one configured (FABLED_EMBEDDINGS_URL). Its presence
   * is what shows the assistant's "Semantic search" toggle.
   */
  embeddings?: { url: string };
}

/** Where semantic search lives, and which archive's clips it covers. */
export interface SemanticConfig {
  url: string;
  archive: string;
}

export function semanticConfig(archives: Archive[]): SemanticConfig | null {
  const a = archives.find((x) => x.embeddings?.url);
  return a ? { url: a.embeddings!.url.replace(/\/+$/, ''), archive: a.key } : null;
}

export const FALLBACK_ARCHIVES: Archive[] = [
  {
    key: 'pad.ma',
    name: 'Pad.ma',
    site: 'https://pad.ma',
    corsMedia: true,
    resolutions: [96, 240, 480],
    formats: ['webm', 'mp4'],
  },
  {
    key: 'indiancine.ma',
    name: 'Indiancine.ma',
    site: 'https://indiancine.ma',
    corsMedia: false,
    resolutions: [240, 480],
    formats: ['webm', 'mp4'],
  },
  {
    key: '0xdb.org',
    name: '0xDB',
    site: 'https://0xdb.org',
    corsMedia: false,
    resolutions: [96],
    formats: ['webm', 'mp4'],
  },
];

let cached: Archive[] | null = null;

export async function fetchArchives(): Promise<Archive[]> {
  if (cached) return cached;
  try {
    const res = await fetch('/api/archives', { credentials: 'include' });
    if (res.ok) {
      const data = (await res.json()) as Archive[];
      if (Array.isArray(data) && data.length > 0 && data[0].key) {
        cached = data;
        return data;
      }
    }
  } catch {
    // backend not running — fall through to the hardcoded registry
  }
  cached = FALLBACK_ARCHIVES;
  return cached;
}

export function archiveByKey(archives: Archive[], key: string): Archive | undefined {
  return archives.find((a) => a.key === key);
}

/**
 * Per-archive annotation layer ids. `layers` is the display order for the text
 * panel; `subtitleLayers` are time-aligned transcript/subtitle layers (each row
 * carries meaningful in/out); `keywordLayers` hold short tag-like values shown
 * as chips.
 *
 * The hardcoded table below covers the three known public instances and remains
 * the synchronous fallback. For any instance, `layerMapFromSiteInfo` derives the
 * same shape from pandora `init` data (`getSiteInfo` in ./pandora.ts), using each
 * layer's `isSubtitles` flag and `type` — self-configuring where callers can
 * afford the async fetch.
 */
export interface ArchiveLayerMap {
  layers: string[];
  subtitleLayers: string[];
  keywordLayers: string[];
}

const ARCHIVE_LAYERS: Record<string, ArchiveLayerMap> = {
  'pad.ma': {
    layers: ['descriptions', 'transcripts', 'keywords', 'events', 'places', 'machinedescriptions'],
    subtitleLayers: ['transcripts'],
    keywordLayers: ['keywords'],
  },
  'indiancine.ma': {
    layers: ['subtitles', 'keywords', 'notes', 'machinedescriptions'],
    subtitleLayers: ['subtitles'],
    keywordLayers: ['keywords'],
  },
  '0xdb.org': {
    layers: ['subtitles', 'tags', 'publicnotes', 'privatenotes'],
    subtitleLayers: ['subtitles'],
    keywordLayers: ['tags'],
  },
};

/** Layer map for an archive key, with a permissive fallback for unknown instances. */
export function archiveLayerMap(key: string): ArchiveLayerMap {
  return (
    ARCHIVE_LAYERS[key] ?? {
      layers: [],
      subtitleLayers: ['subtitles', 'transcripts'],
      keywordLayers: ['keywords', 'tags'],
    }
  );
}

// Id heuristics for instances whose layer definitions omit type/isSubtitles.
const SUBTITLE_LAYER_IDS = new Set(['subtitles', 'transcripts']);
const KEYWORD_LAYER_IDS = new Set(['keywords', 'tags']);

/**
 * Derive an ArchiveLayerMap from pandora `init` site config: layer order as the
 * instance defines it, subtitle layers from the `isSubtitles` flag, keyword
 * layers from `type === 'string'` — with id heuristics matching the hardcoded
 * table when a definition omits the flag/type.
 */
export function layerMapFromSiteInfo(info: PandoraSiteInfo): ArchiveLayerMap {
  const layers = info.layers.map((l) => l.id);
  const subtitleLayers = info.layers
    .filter((l) => l.isSubtitles ?? SUBTITLE_LAYER_IDS.has(l.id))
    .map((l) => l.id);
  const keywordLayers = info.layers
    .filter((l) => (l.type ? l.type === 'string' : KEYWORD_LAYER_IDS.has(l.id)))
    .map((l) => l.id);
  return { layers, subtitleLayers, keywordLayers };
}
